"use client";

import { ALGOLIA_HITS_PER_PAGE, ALGOLIA_PRODUCTS_INDEX } from "@raumagarn/algolia";
import { clsx } from "@trydig/ui";
import { Suspense, useEffect, useRef } from "react";
import { Configure, InstantSearch, useSearchBox } from "react-instantsearch";
import { Search } from "~/components/icons/24/Search";
import { Close } from "~/components/icons/32/Close";
import { searchClient } from "~/features/algolia/client";
import { AlgoliaSearchInput } from "~/features/algolia/components/AlgoliaSearchModal/components/AlgoliaSearchInput";
import { AutoCompleteSuggestions } from "~/features/algolia/components/AlgoliaSearchModal/components/AutoCompleteSuggestions";
import { PopularSearches } from "~/features/algolia/components/AlgoliaSearchModal/components/PopularSearches";
import { SearchResults } from "~/features/algolia/components/AlgoliaSearchModal/components/SearchResults";
import { useAlgoliaSearchModal } from "~/features/algolia/hooks";
import { searchRouting } from "~/features/algolia/routing";
import { Modal } from "~/features/modal";
import headerStyles from "~/features/navigation/Header/header.module.css";
import styles from "./AlgoliaSearchModal.module.css";

const UnsuspendedAlgoliaSearchModal = () => {
	const { isOpen, closeModal, mode, setMode } = useAlgoliaSearchModal();
	const { query, refine, clear } = useSearchBox();

	const lastSearchedValueRef = useRef<string>("");

	useEffect(() => {
		if (mode === "searched" && query !== lastSearchedValueRef.current) {
			setMode("default");
		}
	}, [query, mode, setMode]);

	return (
		<Modal isOpen={isOpen} callback={closeModal} className={styles.root}>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					setMode("searched");
					lastSearchedValueRef.current = query;
				}}
			>
				<div className={styles.header}>
					<div className={styles.closeIcon}>
						<Close onClick={closeModal} />
					</div>
					<AlgoliaSearchInput />
				</div>
			</form>
			<div className={styles.content}>
				<PopularSearches />
				<AutoCompleteSuggestions />
				<SearchResults />
			</div>
		</Modal>
	);
};

export const AlgoliaSearchModal = () => {
	const { isOpen } = useAlgoliaSearchModal();

	if (!isOpen) {
		return null;
	}

	return (
		<Suspense>
			<InstantSearch
				indexName={ALGOLIA_PRODUCTS_INDEX}
				searchClient={searchClient}
				routing={searchRouting}
				insights
			>
				<Configure hitsPerPage={ALGOLIA_HITS_PER_PAGE} clickAnalytics facets={["productType"]} />
				<UnsuspendedAlgoliaSearchModal />
			</InstantSearch>
		</Suspense>
	);
};

export const AlgoliaSearchModalTrigger = () => {
	const { toggleModal } = useAlgoliaSearchModal();

	return (
		<button
			className={clsx(headerStyles.icon, headerStyles.search, styles.button)}
			onClick={toggleModal}
		>
			<Search />
		</button>
	);
};
